import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMField } from "../../../components/utils/PMField";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";
import { SigningPanel } from "../../../components/dashboard/signing/SigningPanel";
import SignatureSignersEditor, {
  normalizeSigners,
} from "../../../components/dashboard/text-documents/SignatureSignersEditor";
import {
  hasConfiguredSigners,
  serializeSigners,
} from "../../../components/dashboard/text-documents/signatureSignerUtils";
import DocumentationSendDialog from "../../../components/dashboard/shared/DocumentationSendDialog";
import DocumentationExportDialog from "../../../components/dashboard/shared/DocumentationExportDialog";
import SendLogPanel from "../../../components/dashboard/shared/SendLogPanel";
import {
  createTextDocument,
  fetchTextDocument,
  fetchTextDocumentPdf,
  sendTextDocument,
  updateTextDocument,
} from "../../../api/textDocuments";

export const TextDocumentPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [post, setPost] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchTextDocument(id).then((data) => {
      setPost(data?.id ? data : null);
      setIsLoading(false);
    });
  }, [id]);

  const save = async (values) => {
    const signers = serializeSigners(values.signature_signers);
    const includeSignature = values.include_signature && signers.length > 0;

    const payload = {
      title: values.title,
      body_html: values.body_html,
      status: values.status,
      include_signature: includeSignature,
      signature_config: { signers },
    };
    const res = post?.id
      ? await updateTextDocument(post.id, payload)
      : await createTextDocument(payload);

    if (res?.errors) {
      toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("textDocuments.save_failed"));
      return;
    }
    toast.success(t("Saved"));
    setPost(res);
    if (!post?.id && res?.id) {
      navigate(`/dashboard/text-documents/${res.id}`, { replace: true });
    }
  };

  const requestPdf = async (opts) => {
    if (!post?.id) {
      toast.error(t("textDocuments.save_before_print"));
      return null;
    }
    setIsPrinting(true);
    try {
      return await fetchTextDocumentPdf(post.id, opts);
    } finally {
      setIsPrinting(false);
    }
  };

  const openPdf = async (opts) => {
    try {
      const res = await requestPdf(opts);
      if (res?.blob) {
        const url = window.URL.createObjectURL(res.blob);
        window.open(url, "_blank", "noopener");
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return res;
      }
      toast.error(t("textDocuments.print_failed"));
      return res;
    } catch (error) {
      toast.error(error?.message || t("textDocuments.print_failed"));
      return null;
    }
  };

  function Content() {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <Link
              to="/dashboard/text-documents"
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t("Back")}
            </Link>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
              <DocumentTextIcon className="h-4 w-4" />
              {post?.id ? t("textDocuments.edit") : t("textDocuments.new")}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
              {post?.title || t("textDocuments.new")}
            </h1>
            {post?.subject_label ? (
              <p className="mt-2 text-sm text-slate-600">{post.subject_label}</p>
            ) : null}
            {post?.last_sent_at ? (
              <p className="mt-3 text-sm font-medium text-emerald-700">
                {t("documentationSend.sent_badge", {
                  date: new Date(post.last_sent_at).toLocaleString(),
                })}
              </p>
            ) : null}
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <Formik
              enableReinitialize
              initialValues={{
                title: post?.title || "",
                body_html: post?.body_html || "",
                status: post?.status || "draft",
                include_signature: post?.include_signature || false,
                signature_signers: normalizeSigners(post?.signature_config?.signers),
              }}
              validationSchema={Yup.object({
                title: Yup.string().max(255).required(t("Title is Required")),
              })}
              onSubmit={async (values, helpers) => {
                await save(values);
                helpers.setSubmitting(false);
              }}
            >
              {(formik) => (
                <Form>
                  <div className="space-y-8 px-6 py-6">
                    <div className="grid gap-6 lg:grid-cols-3">
                      <div className="lg:col-span-2">
                        <PMField
                          name="title"
                          type="text"
                          label={t("Title")}
                          {...formik}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("Status")}
                        </label>
                        <select
                          name="status"
                          value={formik.values.status}
                          onChange={formik.handleChange}
                          className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="draft">{t("textDocuments.status.draft")}</option>
                          <option value="final">{t("textDocuments.status.final")}</option>
                        </select>
                      </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        name="include_signature"
                        checked={formik.values.include_signature}
                        onChange={formik.handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {t("textDocuments.include_signature")}
                    </label>

                    {formik.values.include_signature ? (
                      <SignatureSignersEditor
                        signers={formik.values.signature_signers}
                        onChange={(signers) =>
                          formik.setFieldValue("signature_signers", signers)
                        }
                      />
                    ) : null}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        {t("Content")}
                      </label>
                      <PromangRichTextEditor
                        value={formik.values.body_html}
                        onChange={(value) => formik.setFieldValue("body_html", value)}
                        placeholder={t("textDocuments.editor_placeholder")}
                        size="lg"
                      />
                    </div>

                    {post?.id ? (
                      <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-6">
                        <button
                          type="button"
                          onClick={() => setSendOpen(true)}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                          {t("documentationSend.send_document")}
                        </button>
                        <button
                          type="button"
                          disabled={isPrinting}
                          onClick={() => setExportOpen(true)}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                        >
                          <PrinterIcon className="h-4 w-4" />
                          {t("textDocuments.print")}
                        </button>
                      </div>
                    ) : null}

                    {post?.id ? (
                      <div className="border-t border-slate-100 pt-6">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {t("documentationSend.history_title")}
                        </h3>
                        <SendLogPanel
                          sendLog={post.send_log}
                          lastSentAt={post.last_sent_at}
                          onExportSendLog={() =>
                            openPdf({
                              signature: false,
                              send_log: true,
                              send_log_only: true,
                            })
                          }
                        />
                      </div>
                    ) : null}

                    {post?.id ? (
                      <div className="border-t border-slate-100 pt-6">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {t("textDocuments.signing")}
                        </h3>
                        <SigningPanel
                          signableType="TextDocument"
                          signableId={post.id}
                          title={post.title}
                          nonSensitiveTitle={post.title}
                          documentSigners={formik.values.signature_signers}
                          signingEnabled={
                            formik.values.include_signature &&
                            hasConfiguredSigners(formik.values.signature_signers)
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                  <FormikSaveBar {...formik} />
                  {post?.id ? (
                    <DocumentationExportDialog
                      open={exportOpen}
                      onClose={() => setExportOpen(false)}
                      resourceTitle={post.title}
                      showSignatureOption
                      defaultIncludeSignature={
                        formik.values.include_signature &&
                        hasConfiguredSigners(formik.values.signature_signers)
                      }
                      sendLog={post.send_log}
                      onExport={(opts) => requestPdf(opts)}
                    />
                  ) : null}
                </Form>
              )}
            </Formik>
          </div>

          {post?.id ? (
            <DocumentationSendDialog
              open={sendOpen}
              onClose={() => setSendOpen(false)}
              resourceType="text_document"
              resourceTitle={post.title}
              suggestedRecipients={post.suggested_recipients}
              showSignatureOption
              defaultIncludeSignature={post.include_signature}
              allowMultiple
              onSend={(values) =>
                sendTextDocument(post.id, {
                  email: values.email,
                  name: values.name,
                  message: values.message,
                  signature: values.signature,
                  recipients: values.recipients,
                })
              }
              onSent={(response) => setPost((prev) => ({ ...prev, ...response }))}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <DashboardHeader
      subtitle=""
      actions={[]}
      title={post?.id ? t("textDocuments.edit") : t("textDocuments.new")}
    >
      {isLoading ? <ResourceLoading /> : <Content />}
    </DashboardHeader>
  );
};

export default TextDocumentPage;
